import type { CSSProperties, ReactNode } from 'react'
import '../styles/workspace-skeleton.css'

type SkeletonProps = {
  className?: string
  width?: string | number
  height?: string | number
  circle?: boolean
  style?: CSSProperties
}

function toCssSize(value: string | number | undefined): string | undefined {
  if (value === undefined) return undefined
  return typeof value === 'number' ? `${value}px` : value
}

/** Base shimmer block for workspace loading states. */
export function Skeleton({
  className = '',
  width,
  height,
  circle = false,
  style,
}: SkeletonProps) {
  const merged: CSSProperties = {
    width: toCssSize(width),
    height: toCssSize(height ?? (circle ? width : undefined)),
    ...style,
  }

  return (
    <span
      className={[
        'zk-skeleton',
        circle ? 'zk-skeleton--circle' : 'zk-skeleton--bar',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={merged}
      aria-hidden
    />
  )
}

export function SkeletonText({
  lines = 3,
  widths,
  className = '',
}: {
  lines?: number
  widths?: Array<string | number>
  className?: string
}) {
  const defaults = ['100%', '92%', '78%', '64%']
  return (
    <div className={['zk-skeleton-stack', className].filter(Boolean).join(' ')} aria-hidden>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} width={widths?.[i] ?? defaults[i % defaults.length]} height={12} />
      ))}
    </div>
  )
}

/** Progressive unmask wrapper when a region finishes loading. */
export function SkeletonUnmask({
  ready,
  skeleton,
  children,
  className = '',
}: {
  ready: boolean
  skeleton: ReactNode
  children: ReactNode
  className?: string
}) {
  if (!ready) {
    return (
      <div className={className} aria-busy="true">
        {skeleton}
      </div>
    )
  }
  return <div className={['zk-skeleton-unmask', className].filter(Boolean).join(' ')}>{children}</div>
}

/**
 * Auth-hydration shell mirroring student DashboardLayout:
 * 6px blue chrome, 240px nav rail (icon + label rows), mobile topbar, main pane.
 */
export function WorkspaceShellSkeleton() {
  return (
    <div className="zk-shell-skeleton" role="status" aria-live="polite" aria-busy="true">
      <div className="zk-shell-skeleton__chrome" aria-hidden>
        <div className="zk-shell-skeleton__chrome-bar" />
        <div className="zk-shell-skeleton__chrome-strip" />
      </div>
      <div className="zk-shell-skeleton__mobile-topbar" aria-hidden>
        <Skeleton width={36} height={36} style={{ borderRadius: 8 }} />
        <div className="zk-skeleton-row" style={{ width: 'auto', marginLeft: 'auto' }}>
          <Skeleton width={36} height={36} style={{ borderRadius: 8 }} />
          <Skeleton circle width={32} height={32} />
        </div>
      </div>
      <div className="zk-shell-skeleton__body">
        <aside className="zk-shell-skeleton__sidebar">
          <div className="zk-shell-skeleton__brand">
            <Skeleton width={28} height={28} style={{ borderRadius: 6 }} />
            <Skeleton width={96} height={14} />
          </div>
          <nav className="zk-shell-skeleton__nav" aria-hidden>
            {Array.from({ length: 7 }, (_, i) => (
              <div className="zk-shell-skeleton__nav-item" key={i}>
                <Skeleton width={22} height={22} style={{ borderRadius: 5 }} />
                <Skeleton width={i % 3 === 0 ? '62%' : i % 3 === 1 ? '48%' : '70%'} height={12} />
              </div>
            ))}
          </nav>
          <div className="zk-shell-skeleton__user">
            <Skeleton circle width={36} height={36} />
            <div className="zk-skeleton-stack" style={{ flex: 1 }}>
              <Skeleton width="70%" height={11} />
              <Skeleton width="45%" height={9} />
            </div>
          </div>
        </aside>
        <main className="zk-shell-skeleton__main">
          <div className="zk-shell-skeleton__main-head">
            <Skeleton width="28%" height={22} />
            <Skeleton width="42%" height={12} />
          </div>
          <div className="zk-shell-skeleton__main-tabs">
            <Skeleton width={108} height={34} style={{ borderRadius: 6 }} />
            <Skeleton width={124} height={34} style={{ borderRadius: 6 }} />
            <Skeleton width={112} height={34} style={{ borderRadius: 6 }} />
          </div>
          <div className="zk-shell-skeleton__main-panel">
            <SkeletonText lines={5} widths={['100%', '94%', '88%', '72%', '55%']} />
          </div>
        </main>
      </div>
      <span className="sr-only">Loading workspace…</span>
    </div>
  )
}

/** Conversation list rows — matches .teacher-item (avatar + name + preview). */
export function MessagesSidebarSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <ul className="zk-msg-sidebar-skeleton" aria-hidden>
      {Array.from({ length: rows }, (_, i) => (
        <li className="zk-msg-sidebar-skeleton__item" key={i}>
          <div className="zk-msg-sidebar-skeleton__avatar-wrap">
            <Skeleton circle width={40} height={40} />
            <span className="zk-msg-sidebar-skeleton__status" />
          </div>
          <div className="zk-skeleton-stack" style={{ flex: 1, minWidth: 0 }}>
            <div className="zk-msg-sidebar-skeleton__title-row">
              <Skeleton width={i % 2 === 0 ? '58%' : '46%'} height={12} />
              <Skeleton width={36} height={9} />
            </div>
            <Skeleton width={i % 2 === 0 ? '78%' : '64%'} height={10} />
          </div>
        </li>
      ))}
    </ul>
  )
}

/**
 * Chat column: header strip + message stack + composer — mirrors
 * .chat-header / .chatgpt-chat-history / .lucas-composer / human composer.
 */
export function MessagesChatSkeleton({ bubbles = 4 }: { bubbles?: number }) {
  return (
    <div className="zk-msg-chat-skeleton zk-msg-pane-loading" role="status" aria-live="polite">
      <div className="zk-msg-chat-skeleton__header">
        <div className="zk-skeleton-row" style={{ flex: 1, minWidth: 0 }}>
          <Skeleton circle width={40} height={40} />
          <div className="zk-skeleton-stack" style={{ flex: 1 }}>
            <Skeleton width="36%" height={13} />
            <Skeleton width="22%" height={10} />
          </div>
        </div>
        <div className="zk-skeleton-row" style={{ width: 'auto' }}>
          <Skeleton width={34} height={34} style={{ borderRadius: 8 }} />
          <Skeleton width={34} height={34} style={{ borderRadius: 8 }} />
        </div>
      </div>
      <div className="zk-msg-chat-skeleton__history">
        {Array.from({ length: bubbles }, (_, i) => {
          const self = i % 2 === 1
          return (
            <div
              key={i}
              className={[
                'zk-msg-chat-skeleton__row',
                self ? 'zk-msg-chat-skeleton__row--self' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {!self ? <Skeleton circle width={32} height={32} /> : null}
              <div
                className={[
                  'zk-msg-chat-skeleton__bubble',
                  self ? 'zk-msg-chat-skeleton__bubble--self' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <Skeleton width="100%" height={11} />
                <Skeleton width={self ? '68%' : '86%'} height={11} />
                {!self && i === 0 ? <Skeleton width="52%" height={11} /> : null}
              </div>
            </div>
          )
        })}
      </div>
      <div className="zk-msg-chat-skeleton__composer">
        <Skeleton height={44} style={{ borderRadius: 10, flex: 1 }} />
        <Skeleton width={40} height={40} style={{ borderRadius: 8 }} />
      </div>
      <span className="sr-only">Loading conversation…</span>
    </div>
  )
}

/** My Tutor card grid — 3-col .tutor-cards-section anatomy. */
export function TutorGridSkeleton({ cards = 6 }: { cards?: number }) {
  return (
    <div className="zk-tutor-grid-skeleton" role="status" aria-busy="true">
      {Array.from({ length: cards }, (_, i) => (
        <div className="zk-tutor-card-skeleton" key={i}>
          <div className="zk-tutor-card-skeleton__top">
            <Skeleton circle width={64} height={64} />
            <div className="zk-skeleton-stack" style={{ flex: 1 }}>
              <Skeleton width="72%" height={15} />
              <Skeleton width="40%" height={11} />
              <div className="zk-skeleton-row" style={{ width: 'auto', gap: 6 }}>
                <Skeleton width={52} height={18} style={{ borderRadius: 999 }} />
                <Skeleton width={44} height={18} style={{ borderRadius: 999 }} />
              </div>
            </div>
          </div>
          <div className="zk-tutor-card-skeleton__details">
            <Skeleton width="90%" height={11} />
            <Skeleton width="75%" height={11} />
            <Skeleton width="60%" height={11} />
          </div>
          <div className="zk-tutor-card-skeleton__actions">
            <Skeleton height={36} style={{ borderRadius: 6, flex: 1 }} />
            <Skeleton height={36} style={{ borderRadius: 6, flex: 1 }} />
          </div>
        </div>
      ))}
      <span className="sr-only">Loading tutors…</span>
    </div>
  )
}

/** Account Settings subscription / form panel — label + field rows. */
export function SettingsPanelSkeleton() {
  return (
    <div className="zk-settings-panel-skeleton" role="status" aria-busy="true">
      <div className="zk-settings-panel-skeleton__block">
        <Skeleton width="28%" height={15} />
        <div className="zk-settings-panel-skeleton__row">
          <div className="zk-settings-panel-skeleton__field">
            <Skeleton width="40%" height={11} />
            <Skeleton height={38} style={{ borderRadius: 6 }} />
          </div>
          <div className="zk-settings-panel-skeleton__field">
            <Skeleton width="36%" height={11} />
            <Skeleton height={38} style={{ borderRadius: 6 }} />
          </div>
        </div>
        <div className="zk-settings-panel-skeleton__field">
          <Skeleton width="32%" height={11} />
          <Skeleton height={38} style={{ borderRadius: 6 }} />
        </div>
      </div>
      <div className="zk-settings-panel-skeleton__block">
        <Skeleton width="34%" height={14} />
        <SkeletonText lines={2} widths={['88%', '64%']} />
        <Skeleton width={140} height={36} style={{ borderRadius: 6 }} />
      </div>
      <span className="sr-only">Loading settings…</span>
    </div>
  )
}

/** Overview / tutoring-requests: header + tabs + search/sort + 3-col cards. */
export function SessionListSkeleton({ cards = 4 }: { cards?: number }) {
  return (
    <div className="zk-session-list-skeleton" role="status" aria-busy="true">
      <div className="zk-session-list-skeleton__header">
        <Skeleton width="38%" height={24} />
        <Skeleton width="52%" height={12} />
      </div>
      <div className="zk-skeleton-row zk-session-list-skeleton__tabs">
        <Skeleton width={110} height={36} style={{ borderRadius: 6 }} />
        <Skeleton width={118} height={36} style={{ borderRadius: 6 }} />
        <Skeleton width={124} height={36} style={{ borderRadius: 6 }} />
        <Skeleton width={132} height={36} style={{ borderRadius: 6 }} />
      </div>
      <div className="zk-session-list-skeleton__controls">
        <Skeleton height={40} style={{ borderRadius: 8, flex: 1, maxWidth: 420 }} />
        <Skeleton width={96} height={36} style={{ borderRadius: 6 }} />
        <Skeleton width={96} height={36} style={{ borderRadius: 6 }} />
      </div>
      <div className="zk-session-list-skeleton__cards">
        {Array.from({ length: cards }, (_, i) => (
          <div className="zk-session-card-skeleton" key={i}>
            <Skeleton
              className="zk-session-card-skeleton__tag"
              width={72}
              height={22}
              style={{ borderRadius: 999 }}
            />
            <div className="zk-skeleton-row zk-session-card-skeleton__head">
              <Skeleton circle width={60} height={60} />
              <div className="zk-skeleton-stack" style={{ flex: 1 }}>
                <Skeleton width={i % 2 === 0 ? '62%' : '74%'} height={15} />
                <Skeleton width="44%" height={11} />
              </div>
            </div>
            <div className="zk-session-card-skeleton__meta">
              <Skeleton width="88%" height={11} />
              <Skeleton width="70%" height={11} />
              <Skeleton width="55%" height={11} />
            </div>
            <div className="zk-session-card-skeleton__actions">
              <Skeleton width={100} height={34} style={{ borderRadius: 6 }} />
              <Skeleton width={88} height={34} style={{ borderRadius: 6 }} />
            </div>
          </div>
        ))}
      </div>
      <span className="sr-only">Loading sessions…</span>
    </div>
  )
}

/** My Payments: header + labeled filters + 7-column table. */
export function PaymentsTableSkeleton({ rows = 5 }: { rows?: number }) {
  const colWidths = ['12%', '11%', '10%', '22%', '10%', '12%', '10%']
  return (
    <div className="zk-payments-skeleton" role="status" aria-busy="true">
      <div className="zk-payments-skeleton__header">
        <Skeleton width="28%" height={24} />
        <Skeleton width="44%" height={12} />
      </div>
      <div className="zk-payments-skeleton__filters">
        {Array.from({ length: 3 }, (_, i) => (
          <div className="zk-payments-skeleton__filter" key={i}>
            <Skeleton width="48%" height={10} />
            <Skeleton height={38} style={{ borderRadius: 6 }} />
          </div>
        ))}
        <div className="zk-payments-skeleton__search">
          <Skeleton height={38} style={{ borderRadius: 6, flex: 1 }} />
          <Skeleton width={40} height={38} style={{ borderRadius: 6 }} />
        </div>
      </div>
      <div className="zk-payments-skeleton__table-wrap">
        <div className="zk-payments-skeleton__table-title">
          <Skeleton width="22%" height={16} />
        </div>
        <div className="zk-payments-skeleton__table">
          <div className="zk-payments-skeleton__thead">
            {colWidths.map((w, i) => (
              <Skeleton key={i} width={w} height={11} />
            ))}
          </div>
          {Array.from({ length: rows }, (_, r) => (
            <div className="zk-payments-skeleton__row" key={r}>
              {colWidths.map((w, i) =>
                i === 5 ? (
                  <Skeleton key={i} width={68} height={22} style={{ borderRadius: 999 }} />
                ) : (
                  <Skeleton key={i} width={w} height={12} />
                )
              )}
            </div>
          ))}
        </div>
      </div>
      <span className="sr-only">Loading payments…</span>
    </div>
  )
}

/** Support tickets list column items (list head stays in page chrome). */
export function SupportListSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <ul className="zk-support-list-skeleton" aria-hidden>
      {Array.from({ length: rows }, (_, i) => (
        <li className="zk-support-list-skeleton__item" key={i}>
          <div className="zk-support-list-skeleton__top">
            <Skeleton width={i % 2 === 0 ? '72%' : '58%'} height={13} />
            <Skeleton width={48} height={10} />
          </div>
          <Skeleton width={i % 2 === 0 ? '88%' : '70%'} height={10} />
          <div className="zk-skeleton-row" style={{ marginTop: 2, gap: 8 }}>
            <Skeleton width={68} height={20} style={{ borderRadius: 999 }} />
            <Skeleton width={56} height={20} style={{ borderRadius: 999 }} />
          </div>
        </li>
      ))}
    </ul>
  )
}

/** Support thread: head + gray message body + composer strip. */
export function SupportThreadSkeleton({ bubbles = 3 }: { bubbles?: number }) {
  return (
    <div className="zk-support-thread-skeleton" role="status" aria-busy="true">
      <div className="zk-support-thread-skeleton__head">
        <Skeleton width="48%" height={18} />
        <div className="zk-skeleton-row" style={{ width: 'auto', gap: 8 }}>
          <Skeleton width={72} height={22} style={{ borderRadius: 999 }} />
          <Skeleton width={64} height={22} style={{ borderRadius: 999 }} />
        </div>
      </div>
      <div className="zk-support-thread-skeleton__body">
        {Array.from({ length: bubbles }, (_, i) => {
          const mine = i % 2 === 1
          return (
            <div
              key={i}
              className={[
                'zk-support-thread-skeleton__bubble',
                mine ? 'zk-support-thread-skeleton__bubble--me' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <Skeleton width={mine ? '72%' : '100%'} height={11} />
              <Skeleton width={mine ? '48%' : '84%'} height={11} />
            </div>
          )
        })}
      </div>
      <div className="zk-support-thread-skeleton__composer">
        <Skeleton height={72} style={{ borderRadius: 8, flex: 1 }} />
        <Skeleton width={88} height={36} style={{ borderRadius: 8 }} />
      </div>
      <span className="sr-only">Loading conversation…</span>
    </div>
  )
}

/**
 * School / tutor profile.
 * - school: badge hero + photo/about + contact (SchoolProfile)
 * - tutor: avatar hero + tab strip + body panels (TutorProfile / lightbox)
 */
export function ProfilePageSkeleton({
  variant = 'tutor',
}: {
  variant?: 'school' | 'tutor'
}) {
  if (variant === 'school') {
    return (
      <div className="zk-profile-skeleton zk-profile-skeleton--school" role="status" aria-busy="true">
        <Skeleton width={140} height={14} />
        <div className="zk-profile-skeleton__school-hero">
          <Skeleton width={88} height={88} style={{ borderRadius: 12 }} />
          <div className="zk-skeleton-stack" style={{ flex: 1 }}>
            <Skeleton width="42%" height={24} />
            <Skeleton width="28%" height={12} />
            <Skeleton width="22%" height={12} />
          </div>
        </div>
        <div className="zk-profile-skeleton__school-main">
          <Skeleton
            className="zk-profile-skeleton__school-photo"
            width="100%"
            height={200}
            style={{ borderRadius: 10 }}
          />
          <div className="zk-skeleton-stack" style={{ flex: 1 }}>
            <Skeleton width="36%" height={16} />
            <SkeletonText lines={4} widths={['100%', '96%', '90%', '70%']} />
            <div className="zk-skeleton-row" style={{ flexWrap: 'wrap', gap: 8 }}>
              <Skeleton width={80} height={26} style={{ borderRadius: 999 }} />
              <Skeleton width={96} height={26} style={{ borderRadius: 999 }} />
              <Skeleton width={72} height={26} style={{ borderRadius: 999 }} />
            </div>
          </div>
        </div>
        <div className="zk-profile-skeleton__contact">
          <Skeleton width="30%" height={16} />
          <div className="zk-profile-skeleton__contact-grid">
            {Array.from({ length: 4 }, (_, i) => (
              <div className="zk-profile-skeleton__contact-item" key={i}>
                <Skeleton width={28} height={28} style={{ borderRadius: 6 }} />
                <div className="zk-skeleton-stack" style={{ flex: 1 }}>
                  <Skeleton width="40%" height={10} />
                  <Skeleton width="70%" height={12} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <span className="sr-only">Loading profile…</span>
      </div>
    )
  }

  return (
    <div className="zk-profile-skeleton zk-profile-skeleton--tutor" role="status" aria-busy="true">
      <div className="zk-profile-skeleton__tutor-hero">
        <Skeleton circle width={96} height={96} />
        <div className="zk-skeleton-stack" style={{ flex: 1 }}>
          <Skeleton width="40%" height={22} />
          <Skeleton width="28%" height={12} />
          <SkeletonText lines={2} widths={['85%', '60%']} />
        </div>
      </div>
      <div className="zk-profile-skeleton__tabs">
        <Skeleton width={96} height={34} style={{ borderRadius: 6 }} />
        <Skeleton width={104} height={34} style={{ borderRadius: 6 }} />
        <Skeleton width={112} height={34} style={{ borderRadius: 6 }} />
        <Skeleton width={88} height={34} style={{ borderRadius: 6 }} />
      </div>
      <div className="zk-profile-skeleton__tutor-body">
        <div className="zk-profile-skeleton__card">
          <Skeleton width="32%" height={14} />
          <SkeletonText lines={4} />
        </div>
        <div className="zk-profile-skeleton__card">
          <Skeleton width="28%" height={14} />
          <SkeletonText lines={3} widths={['100%', '85%', '60%']} />
        </div>
      </div>
      <span className="sr-only">Loading profile…</span>
    </div>
  )
}

/** Session / written-explanation detail: back + hero + main | sidebar. */
export function DetailPageSkeleton() {
  return (
    <div className="zk-detail-skeleton" role="status" aria-busy="true">
      <Skeleton width={120} height={14} />
      <div className="zk-detail-skeleton__hero">
        <div className="zk-skeleton-row">
          <Skeleton circle width={56} height={56} />
          <div className="zk-skeleton-stack" style={{ flex: 1 }}>
            <Skeleton width="46%" height={20} />
            <Skeleton width="30%" height={12} />
          </div>
          <Skeleton width={88} height={28} style={{ borderRadius: 999 }} />
        </div>
        <div className="zk-detail-skeleton__hero-meta">
          <Skeleton width="24%" height={11} />
          <Skeleton width="18%" height={11} />
          <Skeleton width="20%" height={11} />
        </div>
      </div>
      <div className="zk-detail-skeleton__columns">
        <div className="zk-detail-skeleton__main">
          <div className="zk-detail-skeleton__card">
            <Skeleton width="28%" height={14} />
            <SkeletonText lines={4} widths={['100%', '95%', '88%', '70%']} />
          </div>
          <div className="zk-detail-skeleton__card">
            <Skeleton width="32%" height={14} />
            <div className="zk-detail-skeleton__detail-grid">
              {Array.from({ length: 4 }, (_, i) => (
                <div className="zk-detail-skeleton__detail-cell" key={i}>
                  <Skeleton width={28} height={28} style={{ borderRadius: 6 }} />
                  <div className="zk-skeleton-stack" style={{ flex: 1 }}>
                    <Skeleton width="50%" height={10} />
                    <Skeleton width="70%" height={12} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="zk-detail-skeleton__card">
            <Skeleton width="24%" height={14} />
            <SkeletonText lines={3} />
          </div>
        </div>
        <aside className="zk-detail-skeleton__sidebar">
          <div className="zk-detail-skeleton__card">
            <Skeleton width="50%" height={14} />
            <Skeleton height={40} style={{ borderRadius: 8 }} />
            <Skeleton height={40} style={{ borderRadius: 8 }} />
          </div>
          <div className="zk-detail-skeleton__card">
            <Skeleton width="44%" height={14} />
            <SkeletonText lines={3} widths={['100%', '80%', '60%']} />
          </div>
        </aside>
      </div>
      <span className="sr-only">Loading details…</span>
    </div>
  )
}

/** Course materials sidebar list — icon tile + name + meta. */
export function CourseSidebarSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="zk-course-sidebar-skeleton" role="status" aria-busy="true">
      {Array.from({ length: rows }, (_, i) => (
        <div className="zk-course-sidebar-skeleton__item" key={i}>
          <Skeleton width={36} height={36} style={{ borderRadius: 8 }} />
          <div className="zk-skeleton-stack" style={{ flex: 1, minWidth: 0 }}>
            <Skeleton width={i % 2 === 0 ? '78%' : '60%'} height={12} />
            <Skeleton width="36%" height={10} />
          </div>
        </div>
      ))}
      <span className="sr-only">Loading courses…</span>
    </div>
  )
}
